import type { DebuggingContext } from "@app/configurable-components/debugging-context/debugging-context";
import type { HTTPClient } from "@app/lib/http-clients/http-client-types";
import type {
  NapiClient,
  NapiClientConstructorOptions
} from "@app/services/api";
import type { InMemoryHttpCache } from "@app/services/utils/in-memory-http-cache";
import type { BBType } from "@app/types/bb-types";
import type { ExperimentsVariations } from "@app/types/experiments";

import type { SupportedLocale } from "../../config/locales";
import type express from "express";

import API from "./api";
import { getGeoHeaders } from "./utils/geo-headers";
import { createHttpClientOptions } from "./utils/http-client";
import { Locales } from "../../config/locales";
import { axiosBrowser } from "../lib/http-clients/axios-browser";

let api_browser: API | null = null;

function createAPI(api_options: NapiClientConstructorOptions) {
  return new API(api_options);
}

function getNapiLanguage(pubweb_locale: SupportedLocale) {
  return Locales.supported_locales[pubweb_locale].napi_lang;
}

export function createApiServer(
  napi_url: string,
  req: express.Request,
  log_levels: { log_level: string | null; NAPI_LOG_LEVEL: string | null },
  experiments: ExperimentsVariations | undefined,
  httpClient: HTTPClient,
  inMemoryHttpCache: InMemoryHttpCache | null,
  debugging_context: DebuggingContext
): NapiClient {
  const http_client_options = createHttpClientOptions(
    req,
    log_levels.log_level,
    experiments
  );

  const api_options: NapiClientConstructorOptions = {
    ...http_client_options,
    domain: napi_url,
    lang: getNapiLanguage(req.url_locale),
    log_level: log_levels.NAPI_LOG_LEVEL ?? undefined,
    httpClient,
    whitelabel_key: req.whitelabel_context?.whitelabel_name,
    legacy_partner_settings: req.whitelabel_context?.legacy_partner_settings,
    inMemoryHttpCache,
    debugging_context
  };

  return createAPI(api_options);
}

export function getApiBrowser(
  napi_url: string,
  BB: BBType,
  httpClient?: HTTPClient
) {
  if (!api_browser) {
    api_browser = createApiBrowser(napi_url, BB, httpClient);
  }
  return api_browser;
}

export function createApiBrowser(
  napi_url: string,
  BB: BBType,
  httpClient?: HTTPClient
) {
  const options: NapiClientConstructorOptions = {
    domain: napi_url,
    token: BB.NAPI_AUTH_TOKEN,
    lang: getNapiLanguage(BB.config.locale),
    locale: BB.config.locale,
    currency: BB.config.currency.code,
    request_id: BB.inst.request_id,
    feature_flags: null, // Only exposed server side.
    whitelabel_key: BB.whitelabel_context?.whitelabel_name,
    legacy_partner_settings: BB.whitelabel_context?.legacy_partner_settings,
    // @ts-expect-error TODO check if value is provided
    log_level: BB.config.NAPI_LOG_LEVEL,
    // @ts-expect-error
    experiments: BB.experiments,
    httpClient: axiosBrowser
  };

  if (httpClient) {
    options.httpClient = httpClient;
  }

  return createAPI(options);
}

/*
 * Creates an API client with Pubweb's master API token
 * Allows privileged server-side API calls, so make sure
 * the controller or middleware using it has proper authorization
 */
export function createApiServerPrivileged(
  req: express.Request,
  experiments: ExperimentsVariations | undefined,
  httpClient: HTTPClient,
  inMemoryHttpCache: InMemoryHttpCache | null,
  debugging_context: DebuggingContext
): NapiClient {
  const api_options: NapiClientConstructorOptions = {
    domain: req.config.BUSBUD_NAPI_URL,
    token: req.config.NAPI_MASTER_AUTH_TOKEN,
    lang: getNapiLanguage(req.url_locale),
    locale: req.url_locale,
    currency: req.currency.code,
    request_id: req.inst.request_id,
    feature_flags: null, // Only exposed server side with user token.
    whitelabel_key: req.whitelabel_context?.whitelabel_name,
    legacy_partner_settings: req.whitelabel_context?.legacy_partner_settings,
    log_level: req.NAPI_LOG_LEVEL ?? undefined,
    experiments,
    geo_headers: getGeoHeaders(req.headers),
    httpClient,
    inMemoryHttpCache,
    debugging_context
  };

  return createAPI(api_options);
}
